import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, CheckCircle, ArrowRight, Mail, Phone } from 'lucide-react';

interface PublicSubmissionsManagerProps {
  onUpdate: () => void;
}

export function PublicSubmissionsManager({ onUpdate }: PublicSubmissionsManagerProps) {
  const { toast } = useToast();
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState<any>(null);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load public submissions
      const { data: submissionsData, error: submissionsError } = await supabase
        .from('public_po_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (submissionsError) throw submissionsError;
      setSubmissions(submissionsData || []);

      // Load districts
      const { data: districtsData, error: districtsError } = await supabase
        .from('districts')
        .select('id, name')
        .order('name');

      if (districtsError) throw districtsError;
      setDistricts(districtsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: "Error",
        description: "Failed to load public submissions",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToPO = (submission: any) => {
    setSelectedSubmission(submission);
    setSelectedDistrict('');
    setShowConvertDialog(true);
  };

  const convertSubmission = async () => {
    if (!selectedSubmission || !selectedDistrict) return;

    setConverting(true);
    try {
      const { data, error } = await supabase.rpc('convert_public_submission_to_po', {
        submission_id: selectedSubmission.id,
        district_id: selectedDistrict
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Public submission converted to purchase order successfully",
      });

      setShowConvertDialog(false);
      setSelectedSubmission(null);
      setSelectedDistrict('');
      loadData();
      onUpdate();
    } catch (error) {
      console.error('Error converting submission:', error);
      toast({
        title: "Error",
        description: "Failed to convert submission to purchase order",
        variant: "destructive",
      });
    } finally {
      setConverting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div>Loading public submissions...</div>
      </div>
    );
  }

  const pendingSubmissions = submissions.filter(s => s.status === 'pending');
  const processedSubmissions = submissions.filter(s => s.status === 'processed');

  return (
    <div className="space-y-6">
      {/* Pending Submissions */}
      <div>
        <h3 className="text-xl font-semibold mb-4">Pending Public Submissions ({pendingSubmissions.length})</h3>
        {pendingSubmissions.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h4 className="text-lg font-semibold mb-2">No Pending Submissions</h4>
                <p className="text-muted-foreground">
                  No new purchase order submissions from the public landing page.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {pendingSubmissions.map((submission) => (
              <Card key={submission.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <CardTitle>PO #{submission.po_number || 'Unknown'}</CardTitle>
                        <Badge variant="secondary">Pending</Badge>
                      </div>
                      <CardDescription>
                        {submission.district_name} • {submission.contact_email}
                        {submission.total_amount && ` • $${submission.total_amount.toFixed(2)}`}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => handleConvertToPO(submission)}
                      >
                        <ArrowRight className="h-4 w-4 mr-2" />
                        Convert to PO
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <h4 className="font-semibold mb-2">Contact Information</h4>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <div className="flex items-center gap-2">
                          <Mail className="h-3 w-3" />
                          <span>{submission.contact_email}</span>
                        </div>
                        {submission.contact_phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-3 w-3" />
                            <span>{submission.contact_phone}</span>
                          </div>
                        )}
                        <p><strong>District:</strong> {submission.district_name}</p>
                      </div>
                    </div>

                    {submission.billing_contact_name && (
                      <div>
                        <h4 className="font-semibold mb-2">Billing Information</h4>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p>{submission.billing_contact_name}</p>
                          {submission.billing_contact_email && <p>{submission.billing_contact_email}</p>}
                          {submission.billing_address && <p>{submission.billing_address}</p>}
                        </div>
                      </div>
                    )}

                    <div>
                      <h4 className="font-semibold mb-2">Submission Details</h4>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <p>Submitted: {new Date(submission.created_at).toLocaleDateString()}</p>
                        {submission.vendor_name && <p>Vendor: {submission.vendor_name}</p>}
                        {submission.po_date && <p>PO Date: {new Date(submission.po_date).toLocaleDateString()}</p>}
                      </div>
                    </div>
                  </div>

                  {submission.notes && (
                    <div className="mt-4">
                      <h4 className="font-semibold mb-2">Notes</h4>
                      <p className="text-sm text-muted-foreground">{submission.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Processed Submissions */}
      {processedSubmissions.length > 0 && (
        <div>
          <h3 className="text-xl font-semibold mb-4">Recently Processed ({processedSubmissions.length})</h3>
          <div className="grid gap-4">
            {processedSubmissions.slice(0, 5).map((submission) => (
              <Card key={submission.id} className="opacity-75">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <CardTitle className="text-base">PO #{submission.po_number || 'Unknown'}</CardTitle>
                    <Badge variant="default">Processed</Badge>
                  </div>
                  <CardDescription>
                    Processed on {new Date(submission.processed_at).toLocaleDateString()}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Convert Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert to Purchase Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select the district to assign this purchase order submission to:
            </p>
            
            <Select value={selectedDistrict} onValueChange={setSelectedDistrict}>
              <SelectTrigger>
                <SelectValue placeholder="Select a district" />
              </SelectTrigger>
              <SelectContent>
                {districts.map((district) => (
                  <SelectItem key={district.id} value={district.id}>
                    {district.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowConvertDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={convertSubmission} 
                disabled={!selectedDistrict || converting}
              >
                {converting ? 'Converting...' : 'Convert to PO'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}